import { useState, useEffect, useRef } from 'react';
import { toast } from '../components/Toast';
import { useParams, useNavigate } from 'react-router-dom';
import { crmApi, STATUS_LABELS } from '../services/crmApi';
import type { CrmApplication } from '../types/crm';

export function useApplicationCore() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<CrmApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);

    const load = async () => {
      if (!id) { setLoading(false); return; }
      try {
        const data = await crmApi.getApplication(parseInt(id));
        if (!mountedRef.current) return;
        setApplication(data);
        setNotes(data.operator_notes || '');
      } catch (error) {
        if (!mountedRef.current) return;
        console.error('Error loading application:', error);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    load();
    return () => { mountedRef.current = false; };
  }, [id]);

  const loadApplication = async () => {
    if (!id) return;
    try {
      const data = await crmApi.getApplication(parseInt(id));
      if (!mountedRef.current) return;
      setApplication(data);
      setNotes(data.operator_notes || '');
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    try {
      await crmApi.updateApplication(parseInt(id), { operator_notes: notes });
      toast.info('Notatki zapisane');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Błąd podczas zapisywania notatek');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      await crmApi.updateStatus(parseInt(id), newStatus);
      await loadApplication();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Błąd podczas zmiany statusu');
    }
  };

  const handleDeleteApplication = async () => {
    if (!id || !application) return;
    const confirmed = window.confirm(
      `⚠️ UWAGA!\n\nCzy na pewno chcesz usunąć ten wniosek?\n\nKandydat: ${application.first_name} ${application.last_name}\nID: ${application.submission_id}\n\nTa operacja jest NIEODWRACALNA i usuńie:\n- Dane wniosku\n- Wykształcenie\n- Zatrudnienie\n- Kursy\n- Planowane zakupy\n- Analizy AI\n- Wszystkie powiązane dane\n\nCzy kontynuować?`
    );
    if (!confirmed) return;
    const doubleConfirm = window.confirm(
      `Ostatnie potwierdźenie!\n\nUsuń wniosek "${application.first_name} ${application.last_name}"?\n\nKliknij OK aby TRWALE USUNĄĆ`
    );
    if (!doubleConfirm) return;
    setSaving(true);
    try {
      await crmApi.deleteApplication(parseInt(id));
      toast.info('✅ Wniosek został usuńięty');
      navigate('/crm');
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('❌ Błąd podczas usuwania wniosku');
    } finally {
      setSaving(false);
    }
  };

  const getCandidateContext = () => {
    if (!application) return '';
    let context = `ID: ${application.submission_id}\nImię i nazwisko: ${application.first_name} ${application.last_name}\nEmail: ${application.email}\nTelefon: ${application.phone}\nStatus: ${STATUS_LABELS[application.status as keyof typeof STATUS_LABELS]}\n\nProfil biznesowy:\n- Branża: ${application.industry}\n- PKD: ${application.pkd_code}\n- Opis pomysłu: ${application.business_idea_raw}\n- Kwota wnioskowana: ${application.requested_amount?.toLocaleString('pl-PL')} zł\n`;
    if ((application.education?.length ?? 0) > 0) {
      context += '\nWykształcenie:\n';
      application.education!.forEach((edu) => {
        context += `- ${edu.education_level}, ${edu.school_name}, rok ${edu.completion_year}\n`;
      });
    }
    if ((application.employment?.length ?? 0) > 0) {
      context += '\nDoświadczenie zawodowe:\n';
      application.employment!.forEach((emp) => {
        context += `- ${emp.position} w ${emp.company_name} (${emp.period_from} - ${emp.period_to})\n`;
      });
    }
    if (application.warnings?.hasWarnings) {
      context += '\n⚠️ OSTRZEŻENIA:\n';
      application.warnings.issues.forEach((issue: string) => { context += `- ${issue}\n`; });
    }
    if (application.aiAnalysis) {
      context += `\nAnaliza AI:\nOcena ogólna: ${application.aiAnalysis.overall_assessment}\n`;
    }
    return context;
  };

  return {
    id, navigate,
    application, setApplication, loading, notes, setNotes, saving, setSaving,
    mountedRef,
    loadApplication,
    handleSaveNotes, handleStatusChange, handleDeleteApplication,
    getCandidateContext,
  };
}
